import { useEffect, useRef } from "react"
import { coreGet } from "@/services/coreApi"
import { useAccessContext } from "@/contexts/AccessContext"
import { defaultConsumptionProfile } from "@/config/defaults"

// Loads the selected vessel's consumption profile into the planner state.
// Core returns it request-ready (`profile`: values + referenceDraft /
// draftDependency from the ShippingDB) — the FE never reshapes it. No
// vessel, empty result, or a failed fetch falls back to the default profile.
//
// currentDraft is prefilled with the profile's referenceDraft (correction is
// neutral there — same result as sending no draft trio) until daily-report
// data is available. A manual edit always wins: the prefill only overwrites
// an empty field or its own previous auto value.
export default function useVesselConsumptionProfile({
  vesselId,
  setConsumptionProfileData,
  currentDraft,
  setCurrentDraft,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  const requestIdRef = useRef(0)
  const lastAutoDraftRef = useRef(null)
  const currentDraftRef = useRef(currentDraft)
  currentDraftRef.current = currentDraft

  useEffect(() => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    const applyProfile = (profile) => {
      setConsumptionProfileData(profile)
      if (!setCurrentDraft) return
      const untouched =
        (currentDraftRef.current ?? "") === "" ||
        currentDraftRef.current === lastAutoDraftRef.current
      if (!untouched) return
      const referenceDraft = Number(profile?.referenceDraft)
      const next = referenceDraft > 0 ? String(referenceDraft) : ""
      setCurrentDraft(next)
      lastAutoDraftRef.current = next
    }

    const numericVesselId = Number(vesselId)
    if (!Number.isFinite(numericVesselId)) {
      applyProfile(defaultConsumptionProfile)
      return
    }

    const load = async () => {
      try {
        const response = await coreGet(
          `/consumption-profiles?vessel_id=${numericVesselId}`,
          accessContext,
          setAccessContext,
          ".core /consumption-profiles (planner) error=",
        )
        if (requestIdRef.current !== requestId) return
        const profile = response?.profile
        applyProfile(
          profile?.values?.length ? profile : defaultConsumptionProfile,
        )
      } catch (error) {
        console.error(error)
        if (requestIdRef.current !== requestId) return
        applyProfile(defaultConsumptionProfile)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId, setConsumptionProfileData, setCurrentDraft])
}
